import { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Copy, Check, MessageCircle, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { getAnnouncementShareUrl } from '@/lib/shareUrl';
import { openSms, copyWithToast } from '@/lib/shareLaunch';

const confessionShareCopy = {
  en: {
    title: 'Your confession is ready 💌',
    subtitle: 'Share this private link directly with the person you want to see it.',
    privacy: 'This link is the only way to access your confession. It won\'t appear in any public listing.',
    copyLink: 'Copy Private Link',
    copied: 'Link copied — share it when you\'re ready',
    sendSms: 'Send via Text',
    nativeShare: 'Share Privately',
    reassurance: 'Nothing is shared until you choose. You\'re in control.',
  },
  es: {
    title: 'Tu confesión está lista 💌',
    subtitle: 'Comparte este enlace privado directamente con la persona que quieres que lo vea.',
    privacy: 'Este enlace es la única forma de acceder a tu confesión. No aparecerá en ningún listado público.',
    copyLink: 'Copiar Enlace Privado',
    copied: 'Enlace copiado — compártelo cuando estés listo/a',
    sendSms: 'Enviar por Texto',
    nativeShare: 'Compartir Privadamente',
    reassurance: 'Nada se comparte hasta que tú lo decidas. Tú tienes el control.',
  },
  fr: {
    title: 'Votre confession est prête 💌',
    subtitle: 'Partagez ce lien privé directement avec la personne concernée.',
    privacy: 'Ce lien est le seul moyen d\'accéder à votre confession. Il n\'apparaîtra dans aucune liste publique.',
    copyLink: 'Copier le Lien Privé',
    copied: 'Lien copié — partagez-le quand vous êtes prêt',
    sendSms: 'Envoyer par SMS',
    nativeShare: 'Partager en Privé',
    reassurance: 'Rien n\'est partagé sans votre accord. Vous gardez le contrôle.',
  },
  pt: {
    title: 'Sua confissão está pronta 💌',
    subtitle: 'Compartilhe este link privado diretamente com a pessoa que você quer que veja.',
    privacy: 'Este link é a única forma de acessar sua confissão. Não aparecerá em nenhum listado público.',
    copyLink: 'Copiar Link Privado',
    copied: 'Link copiado — compartilhe quando estiver pronto/a',
    sendSms: 'Enviar por Texto',
    nativeShare: 'Compartilhar Privadamente',
    reassurance: 'Nada é compartilhado até você decidir. Você está no controle.',
  },
  ar: {
    title: 'اعترافك جاهز 💌',
    subtitle: 'شارك هذا الرابط الخاص مباشرة مع الشخص الذي تريد أن يراه.',
    privacy: 'هذا الرابط هو الطريقة الوحيدة للوصول إلى اعترافك. لن يظهر في أي قائمة عامة.',
    copyLink: 'نسخ الرابط الخاص',
    copied: 'تم نسخ الرابط — شاركه عندما تكون مستعداً',
    sendSms: 'إرسال عبر رسالة نصية',
    nativeShare: 'مشاركة بخصوصية',
    reassurance: 'لا شيء يُشارك حتى تختار. أنت في السيطرة.',
  },
  ja: {
    title: 'あなたの告白の準備ができました 💌',
    subtitle: '見てほしい人にこのプライベートリンクを直接共有してください。',
    privacy: 'このリンクがあなたの告白にアクセスする唯一の方法です。公開リストには表示されません。',
    copyLink: 'プライベートリンクをコピー',
    copied: 'リンクをコピーしました — 準備ができたら共有してください',
    sendSms: 'テキストで送信',
    nativeShare: 'プライベートに共有',
    reassurance: 'あなたが選択するまで何も共有されません。あなたがコントロールしています。',
  },
  ko: {
    title: '당신의 고백이 준비되었습니다 💌',
    subtitle: '보여주고 싶은 사람에게 이 비공개 링크를 직접 공유하세요.',
    privacy: '이 링크가 당신의 고백에 접근하는 유일한 방법입니다. 공개 목록에는 표시되지 않습니다.',
    copyLink: '비공개 링크 복사',
    copied: '링크가 복사되었습니다 — 준비가 되면 공유하세요',
    sendSms: '문자로 보내기',
    nativeShare: '비공개로 공유',
    reassurance: '당신이 선택할 때까지 아무것도 공유되지 않습니다. 당신이 통제합니다.',
  },
  id: {
    title: 'Pengakuan Anda sudah siap 💌',
    subtitle: 'Bagikan tautan pribadi ini langsung kepada orang yang ingin Anda tuju.',
    privacy: 'Tautan ini adalah satu-satunya cara mengakses pengakuan Anda. Tidak akan muncul di daftar publik.',
    copyLink: 'Salin Tautan Pribadi',
    copied: 'Tautan disalin — bagikan saat Anda siap',
    sendSms: 'Kirim via Teks',
    nativeShare: 'Bagikan Secara Pribadi',
    reassurance: 'Tidak ada yang dibagikan sampai Anda memilih. Anda yang mengendalikan.',
  },
};

interface ConfessionShareProps {
  publicSlug: string;
}

export function ConfessionShare({ publicSlug }: ConfessionShareProps) {
  const { language } = useLanguage();
  const c = lc(confessionShareCopy, language);
  const [copied, setCopied] = useState(false);
  const shareUrl = getAnnouncementShareUrl(publicSlug);

  const handleCopy = async () => {
    const ok = await copyWithToast(shareUrl, c.copied, 'Failed to copy');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleSms = () => {
    openSms(shareUrl);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: shareUrl });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') await handleCopy();
      }
    } else {
      await handleCopy();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-md mx-auto space-y-5"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-primary/10 text-primary mb-2">
          <Lock className="h-7 w-7" />
        </div>
        <h3 className="text-xl font-bold text-foreground">{c.title}</h3>
        <p className="text-sm text-muted-foreground">{c.subtitle}</p>
      </div>

      {/* Privacy badge */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-start gap-2.5">
        <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">{c.privacy}</p>
      </div>

      {/* Link + copy */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input readOnly value={shareUrl} className="flex-1 bg-muted text-sm font-mono" />
          <Button onClick={handleCopy} variant="outline" size="icon" className="shrink-0">
            {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>

        <Button onClick={handleCopy} className="w-full gap-2">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {c.copyLink}
        </Button>

        <div className="grid grid-cols-2 gap-2">
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <Button onClick={handleNativeShare} variant="outline" className="gap-2">
              <Share2 className="h-4 w-4" />
              {c.nativeShare}
            </Button>
          )}
          <Button onClick={handleSms} variant="outline" className="gap-2">
            <MessageCircle className="h-4 w-4" />
            {c.sendSms}
          </Button>
        </div>
      </div>

      {/* Reassurance */}
      <p className="text-xs text-center text-muted-foreground/70">{c.reassurance}</p>
    </motion.div>
  );
}
